import { Property } from "@app/models/Property"
import { makeAuthenticatedRequest, RequestMethod } from "@app/utils/request"
import type { SendFileAPIResponse } from "stream-chat/dist/types"

/**
 * The body of a request to create or update a channel.
 */
type ChannelBody = {
  name: string,
  image: string,
  desiredMembers: string[],
  isPublic: boolean,
}

export type APIKeyAndToken = {
  apiKey: string,
  token: string,
}

/**
 * Request a Getstream chat API key and token from the server
 * using the current user's HB identity to authenticate.
 * @returns A promise that resolves with an object containing apiKey and token, or an Error if failed.
 */
export async function issueAPIKeyAndToken(): Promise<APIKeyAndToken | Error> {
  try {
    const response = await makeAuthenticatedRequest('/api/Chat/IssueAPIKeyAndToken')
    
    if (response.success && response.message) {
      try {
        const data = JSON.parse(response.message)
        if (data.apiKey && data.token) {
          return {
            apiKey: data.apiKey,
            token: data.token
          }
        }
      } catch (parseError) {
        return new Error('Failed to parse API key and token response')
      }
    }
    
    return new Error(response.message || 'Failed to issue API key and token')
  } catch (error) {
    return error instanceof Error ? error : new Error('Failed to issue API key and token')
  }
}

/**
 * Updates permissions and channels for the user based on their
 * HB identity, but must be called after the chat client is connected.
 * @returns A promise that resolves when the user is synced.
 */
export function syncUser() {
  return makeAuthenticatedRequest('/api/Chat/SyncUser', {
    method: RequestMethod.POST
  })
}

export async function createChannel(body: ChannelBody) {
  return await makeAuthenticatedRequest('/api/Chat/Channel', {
    method: RequestMethod.POST,
    body: JSON.stringify(body),
  })
}

export async function updateChannel(id: string, body: ChannelBody) {
  return await makeAuthenticatedRequest(`/api/Chat/Channel/${id}`, {
    method: RequestMethod.PUT,
    body: JSON.stringify(body),
  })
}

export async function archiveChannel(id: string) {
  return await makeAuthenticatedRequest(`/api/Chat/Channel/${id}/Archive`, {
    method: RequestMethod.POST,
  })
}

export async function unarchiveChannel(id: string) {
  return await makeAuthenticatedRequest(`/api/Chat/Channel/${id}/Unarchive`, {
    method: RequestMethod.POST,
  })
}

export async function uploadFiles(files: File[]) {
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files[]', file)
  })
  return await makeAuthenticatedRequest('/api/Chat/UploadFiles', {
    method: RequestMethod.POST,
    body: formData,
    noDefaultHeaders: true, // The browser must set Content-Type and boundary automatically
  })
}

export async function handleFileUpload(file: File): Promise<SendFileAPIResponse> {
  const response = await uploadFiles([file])
  if (!response.success || !response.fileUrls || response.fileUrls.length === 0) {
    throw new Error('Error uploading file: ' + (response.message ?? 'internal server error'));
  }
  return {
    duration: response.duration,
    file: response.fileUrls[0],
    thumb_url: response.fileUrls[0],
  }
}

export async function getPropertyById(id: string): Promise<Property> {
  const response = await makeAuthenticatedRequest(`/api/Properties/${id}`)
  if (!response.success || !response.entity) {
    throw new Error('Error fetching property: ' + (response.message ?? 'internal server error'))
  }
  return Property.fromJS(response.entity)
}
